import { FC, useCallback, useMemo } from "react";
import { PlusIcon } from "@icons";
import { Command } from "@vendor/cmdk/cmdk";
import { useOverlays } from "contexts/OverlayContext";
import { TaskType } from "@common/types/task";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useEntitiesWithAccountingAvailable } from "@components/accounting/useEntitiesWithAccountingAvailable";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { useCommands } from "./useCommands";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { BankCashItemType } from "@common/types/bankCashItem";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { AddEntityCommandItem } from "@components/addEntity/AddEntityCommandItem";
import {
    useCanAddCashItems,
    useOpenAddTransactionType,
} from "@components/checks/AddTransactionTypePicker";
import { CommandsDirectory } from "./lib";
import { StartCloseAgentCommandItem } from "./StartCloseAgentCommandItem";
import { InvoicingAccess } from "@components/invoices/InvoicingAccess";

export const AddWorkspaceItemsCommandsList: FC = () => {
    const navigate = useStateAwareNavigate();
    const { close: closeCommands, open: changeDirectory } = useCommands();
    const canAddMember = useCanPerformWorkspaceAction(
        WorkspaceAction.MEMBERS_EDIT,
    );
    const canCreateEntities = useCanPerformWorkspaceAction(
        WorkspaceAction.ENTITY_CREATE,
    );
    const { openOverlay } = useOverlays();
    const entitiesWithAccountingAvailable =
        useEntitiesWithAccountingAvailable();

    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const canAddCashItems = useCanAddCashItems();
    const openAddTransactionType = useOpenAddTransactionType();
    const { agentRuns: isAgentRunsEnabled } = useTypedFlags();

    const onAddTask = useCallback(() => {
        // Opens a create form the server can only refuse on save (PRO-11345).
        if (refuseIfViewOnly()) {
            return;
        }
        openOverlay("createTask", {
            type: TaskType.QUESTION,
        });
        closeCommands();
    }, [closeCommands, openOverlay, refuseIfViewOnly]);

    const onAddAccount = useCallback(
        () => changeDirectory(CommandsDirectory.ACCOUNTS),
        [changeDirectory],
    );

    const onAddTransaction = useCallback(() => {
        closeCommands();
        openOverlay("addTransactions", true);
    }, [closeCommands, openOverlay]);

    const onAddCashItem = (type: BankCashItemType) => {
        if (refuseIfViewOnly()) {
            return;
        }
        closeCommands();
        openAddTransactionType(type);
    };

    const onCreateInvoice = useCallback(() => {
        // Same as the Invoicing page's own create entries: the drawer's save is
        // the first thing the server sees.
        if (refuseIfViewOnly()) {
            return;
        }
        closeCommands();
        navigate("/invoicing");
        openOverlay("nativeInvoiceDrawer", {});
    }, [closeCommands, navigate, openOverlay, refuseIfViewOnly]);

    const onAddMember = useCallback(() => {
        if (canAddMember) {
            closeCommands();
            openOverlay("memberManagementAddMember", {});
        }
    }, [canAddMember, closeCommands, openOverlay]);

    const showAddJournalEntry = useMemo(
        () => entitiesWithAccountingAvailable.length > 0,
        [entitiesWithAccountingAvailable],
    );

    const onAddJournalEntry = useCallback(() => {
        const firstEntity = entitiesWithAccountingAvailable[0];

        if (!firstEntity) {
            return;
        }

        closeCommands();
        openOverlay("journalEntryDrawer", {
            entity: firstEntity,
        });
    }, [closeCommands, openOverlay, entitiesWithAccountingAvailable]);

    return (
        <Command.Group heading="Actions">
            <CommandItem
                onSelect={onAddAccount}
                keywords={["create", "new"]}
                shortcut="a"
            >
                <PlusIcon /> Add account
            </CommandItem>
            {canCreateEntities && (
                <AddEntityCommandItem onSelected={closeCommands} />
            )}
            {canAddMember && (
                <CommandItem
                    onSelect={onAddMember}
                    keywords={["create", "new", "accountant", "admin"]}
                    shortcut="m"
                >
                    <PlusIcon /> Add member
                </CommandItem>
            )}
            <CommandItem
                onSelect={onAddTransaction}
                keywords={["create", "new", "transaction"]}
                shortcut="t"
            >
                <PlusIcon /> Add transaction
            </CommandItem>
            {canAddCashItems && (
                <>
                    <CommandItem
                        onSelect={() => onAddCashItem(BankCashItemType.CHECK)}
                        keywords={["create", "new", "cheque", "record"]}
                        shortcut="c"
                    >
                        <PlusIcon /> Add check
                    </CommandItem>
                    <CommandItem
                        onSelect={() => onAddCashItem(BankCashItemType.DEPOSIT)}
                        keywords={["create", "new", "record"]}
                        shortcut="d"
                    >
                        <PlusIcon /> Add deposit
                    </CommandItem>
                </>
            )}
            <CommandItem
                onSelect={onAddTask}
                keywords={["create", "new", "task"]}
                shortcut="q"
            >
                <PlusIcon /> Create task
            </CommandItem>
            {showAddJournalEntry && (
                <CommandItem
                    onSelect={onAddJournalEntry}
                    keywords={["create", "new", "journal entry"]}
                    shortcut="j"
                >
                    <PlusIcon /> Add journal entry
                </CommandItem>
            )}
            <InvoicingAccess>
                <CommandItem
                    onSelect={onCreateInvoice}
                    keywords={["create", "new", "invoice", "bill"]}
                    shortcut="i"
                >
                    <PlusIcon /> Create invoice
                </CommandItem>
            </InvoicingAccess>
            {isAgentRunsEnabled && <StartCloseAgentCommandItem />}
        </Command.Group>
    );
};
